/**
 * The escrowbuild.io auth contract.
 *
 * This is the only module that knows the API's auth shape: the provider ids,
 * the consent URL, the token envelope and the calls on it. Everything else in
 * the app asks this module rather than spelling a route or an envelope field
 * itself, so a change to the frozen contract is a change in one file.
 *
 * The frontend NEVER talks to a provider. Consent runs on the provider's own
 * origin and the API drives it, so nothing here renders provider UI — a button
 * hands the browser to the API and the API does the rest.
 */

export type ProviderId = 'google-oauth2' | 'microsoft-graph' | 'apple-id';

/**
 * Read once, at import. The two places that use it — the consent URL and the
 * refresh — must agree on the host, and reading an env var per call site is how
 * they stop agreeing.
 */
export const API_URL: string =
  (import.meta.env.VITE_ESCROWBUILD_API_URL as string | undefined) ?? 'http://localhost:8010';

/**
 * The callback is this app's OWN route, so it is derived from the origin the
 * app is actually served on rather than from a build-time variable. That origin
 * is what the API's `AUTH_REDIRECT_URIS` allowlist has to carry anyway, so the
 * two cannot drift: a mistyped variable is a login the API refuses to redirect
 * with nothing on screen to say why.
 */
export const CALLBACK_URI = `${window.location.origin}/auth/callback`;

/**
 * A failure this module decided, carried as the KEY of the sentence rather than
 * as the sentence.
 *
 * The two are not the same thing and the difference is the whole point: a
 * finished string thrown from here has already chosen a language by the time
 * anything can render it, and the sign-in screen — which cannot know which of
 * its failures were ours and which came off the wire — renders it verbatim. A
 * Portuguese reader was shown this module's English for exactly that reason.
 *
 * A key travels instead, and the screen resolves it against the catalogue of the
 * locale being rendered. Text the API itself supplied still passes through
 * untranslated: that is deliberate, because it is not ours to translate.
 */
export type ExchangeFailureKey = 'auth.networkError' | 'auth.exchangeFailed';

export class ExchangeFailure extends Error {
  constructor(
    readonly key: ExchangeFailureKey,
    /** The HTTP status, for the one key whose sentence names it. */
    readonly status?: number,
  ) {
    super(key);
    this.name = 'ExchangeFailure';
  }
}

/** `{API}/auth/login/{backend}/?redirect_uri=&state=` — the consent URL. */
export function loginUrl(backend: ProviderId, state: string, redirectUri: string): string {
  const query = new URLSearchParams({ redirect_uri: redirectUri, state });
  return `${API_URL}/auth/login/${backend}/?${query.toString()}`;
}

// ── The token envelope ──────────────────────────────────────────────────────

export interface TenantBrief {
  id: number;
  name: string;
}

export interface SessionUser {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  /** The provider's picture URL. `''` — never absent — when it gave none. */
  photo: string;
  is_staff: boolean;
  is_superuser: boolean;
  /** `null` for a solo user on a consumer email domain. */
  tenant: TenantBrief | null;
}

/**
 * `result: "failure"` with a `message` is a 200, not a 4xx: the exchange
 * succeeded and the sign-in failed. Only a malformed request is a 4xx, so a
 * caller that only checks `response.ok` renders the wrong screen.
 */
export interface TokenEnvelope {
  provider: string;
  result: 'success' | 'failure';
  reason: string | null;
  message: string | null;
  access?: string;
  refresh?: string;
  user?: SessionUser;
}

/** The name to put in a header: what the provider knows, then the fallbacks. */
export function displayName(user: SessionUser): string {
  const full = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
  return full || user.email || user.username;
}

// ── The one-time code ───────────────────────────────────────────────────────

/**
 * One exchange per code, keyed by the code it was made with.
 *
 * The code is claimed exactly once. React StrictMode mounts, unmounts and
 * mounts again in development, and the second mount would otherwise POST the
 * same code — which the server has already consumed, so the second POST fails
 * and a visitor who did nothing wrong is shown a failure. Caching the PROMISE
 * rather than a boolean lets the remount reuse the first exchange instead.
 *
 * Entries are deliberately never evicted: dropping a settled one would let a
 * later remount re-POST a burnt code, which is the exact defect this prevents.
 */
const exchanges = new Map<string, Promise<TokenEnvelope>>();

export function exchangeCode(code: string): Promise<TokenEnvelope> {
  const claimed = exchanges.get(code);
  if (claimed) return claimed;

  const exchange = (async (): Promise<TokenEnvelope> => {
    let response: Response;
    try {
      response = await fetch(`${API_URL}/auth/token/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ code }),
      });
    } catch {
      throw new ExchangeFailure('auth.networkError');
    }
    const envelope = (await response.json().catch(() => null)) as TokenEnvelope | null;
    if (!envelope) {
      throw new ExchangeFailure('auth.exchangeFailed', response.status);
    }
    return envelope;
  })();

  exchanges.set(code, exchange);
  return exchange;
}

// ── The `state` nonce ───────────────────────────────────────────────────────

const STATE_KEY = 'escrowbuild:auth-state';

/**
 * Start a sign-in: mint the nonce, keep it, and leave for the API.
 *
 * THIS is the one place that decides a provider button leaves the app. Consent
 * is another origin, so it is a top-level navigation and not a router push —
 * the app must be gone before the provider's screen arrives.
 *
 * The nonce is the round trip's own proof: the API echoes it back, and the
 * callback refuses a code that comes with a nonce this tab never sent.
 */
export function beginSignIn(backend: ProviderId): void {
  const state = crypto.randomUUID();
  try {
    sessionStorage.setItem(STATE_KEY, state);
  } catch {
    // Private mode with storage disabled: the callback will refuse the code
    // rather than accept one it cannot verify.
  }
  window.location.assign(loginUrl(backend, state, CALLBACK_URI));
}

/** Reads and clears — the nonce is single-use, so it cannot verify twice. */
export function takeStateNonce(): string | null {
  try {
    const state = sessionStorage.getItem(STATE_KEY);
    sessionStorage.removeItem(STATE_KEY);
    return state;
  } catch {
    return null;
  }
}

// ── The other three calls ───────────────────────────────────────────────────

/** `POST /auth/refresh/` — a fresh access token, or `null` if refused. */
export async function refreshAccessToken(refresh: string): Promise<string | null> {
  try {
    const response = await fetch(`${API_URL}/auth/refresh/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ refresh }),
    });
    if (!response.ok) return null;
    const body = (await response.json().catch(() => null)) as { access?: string } | null;
    return body?.access ?? null;
  } catch {
    return null;
  }
}

/** `POST /auth/logout/` — blacklist the refresh token. */
export async function logoutSession(refresh: string): Promise<void> {
  await fetch(`${API_URL}/auth/logout/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh }),
  });
}

/** `GET /auth/me/` — the signed-in profile, or `null` if not authorised. */
export async function fetchMe(access: string): Promise<SessionUser | null> {
  try {
    const response = await fetch(`${API_URL}/auth/me/`, {
      headers: { Authorization: `Bearer ${access}`, Accept: 'application/json' },
    });
    if (!response.ok) return null;
    return (await response.json().catch(() => null)) as SessionUser | null;
  } catch {
    return null;
  }
}
